import {
  AppSessionId,
  CREATE_GRID_LAYOUT,
  CREATE_GRID_ROW,
  CellExecutionState,
  CellId,
  DELETE_GRID_LAYOUT,
  FRACTIONAL_INDEX_END,
  FRACTIONAL_INDEX_START,
  GridColumnId,
  GridElementEntityId,
  GridElementId,
  GridElementType,
  GridLayoutId,
  GridRowId,
  HexVersionId,
  StaticCellId,
  StaticGridLayoutId,
  fractionalIndexMidpoint95,
  getDateTimeString,
  typedObjectEntries,
  typedObjectFromEntries,
  uuid,
} from "@hex/common";
import Slugger from "github-slugger";
import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  useGridLayoutsGetter,
  useGridLayoutsSelector,
} from "../../../hex-version-multiplayer/state-hooks/gridLayoutsStateHooks.js";
import { useGridRowsGetter } from "../../../hex-version-multiplayer/state-hooks/gridRowsStateHooks.js";
import { useDispatch, useSelector } from "../../../redux/hooks.js";
import { appSessionMPSelectors } from "../../../redux/slices/appSessionMPSlice.js";
import {
  GridLayoutMP,
  GridRowMP,
  hexVersionMPSelectors,
} from "../../../redux/slices/hexVersionMPSlice.js";
import { setDeleteTabDialog } from "../../../redux/slices/logicViewSlice.js";
import { RootState } from "../../../redux/store.js";
import {
  createSelector,
  createShallowResultSelector,
} from "../../../redux/utils/index.js";
import {
  SelectionNavigationState,
  mergeSelectionNavigationState,
  useQueryParams,
} from "../../../route/routes.js";
import { useHexVersionAOContext } from "../../../util/hexVersionAOContext.js";
import { useProjectContext } from "../../../util/projectContext.js";
import { useSessionContext } from "../../../util/sessionContext.js";

import { gridRowMPToGridRow } from "./utils/gridUtils.js";

const TAB_QUERY_PARAM = "tab";

const selectPrimaryTab = (tabs: GridLayoutMP[]): GridLayoutMP | undefined =>
  tabs[0];

export const selectPrimaryTabId = (
  tabs: GridLayoutMP[],
): GridLayoutId | undefined => selectPrimaryTab(tabs)?.id;

export const selectTabIdToSlug = createSelector(
  [(tabs: GridLayoutMP[]) => tabs],
  (tabs: GridLayoutMP[]): Record<GridLayoutId, string> => {
    const slugger = new Slugger();
    return typedObjectFromEntries(
      tabs
        .map((gl) => [gl.id, slugger.slug(gl.name)] as const)
        .concat([[TITLE_SCREEN.id, TITLE_SCREEN_SLUG] as const]),
    );
  },
);

const selectSlugToTabId = createSelector(
  [selectTabIdToSlug],
  (
    idsToSlugs: Record<GridLayoutId, string>,
  ): Record<string, GridLayoutId | undefined> =>
    typedObjectFromEntries(
      typedObjectEntries(idsToSlugs)
        .map(([id, slug]) => [slug, id] as const)
        .concat([[TITLE_SCREEN_SLUG, TITLE_SCREEN.id] as const]),
    ),
);

const selectSelectedTabId = createSelector(
  [
    selectPrimaryTabId,
    selectSlugToTabId,
    (_: GridLayoutMP[], queryParams: URLSearchParams) => queryParams,
  ],
  (primaryTabId, slugsToIds, queryParams) => {
    const maybeSelectedTabSlug = queryParams.get(TAB_QUERY_PARAM);
    if (maybeSelectedTabSlug == null) {
      return primaryTabId;
    }

    const maybeSelectedTabId = slugsToIds[maybeSelectedTabSlug];
    if (maybeSelectedTabId == null) {
      return primaryTabId;
    }

    return maybeSelectedTabId;
  },
);

// Placeholder title tab used for presentation mode
export const TITLE_SCREEN: GridLayoutMP = {
  __typename: "GridLayout",
  id: "fake-title-tab-id" as GridLayoutId,
  name: "Title Slide",
  order: "      ",
  revision: 1,
  createdDate: getDateTimeString(new Date()),
  updatedDate: getDateTimeString(new Date()),
  deletedDate: null,
  staticId: "fake-title-tab-id" as StaticGridLayoutId,
};

const TITLE_SCREEN_SLUG = "title-screen";

const selectTabIdToCellExecutionState = createShallowResultSelector(
  [
    (
      state: RootState,
      hexVersionId: HexVersionId,
      _appSessionId: AppSessionId,
    ) =>
      hexVersionMPSelectors
        .getGridRowSelectors(hexVersionId)
        .selectAll(state) ?? [],
    (
      state: RootState,
      _hexVersionId: HexVersionId,
      appSessionId: AppSessionId,
    ) =>
      appSessionMPSelectors
        .getAppSessionCellSelectors(appSessionId)
        .selectAll(state) ?? [],
  ],
  (gridRows, appSessionCells) => {
    const cellIdToRunState = appSessionCells.reduce<
      Record<CellId, CellExecutionState>
    >((acc, appSessionCell) => {
      acc[appSessionCell.cellId] = appSessionCell.state;
      return acc;
    }, {});

    const tabIdToCellIds = gridRows.reduce<Record<GridLayoutId, Set<CellId>>>(
      (acc, gridRow) => {
        const prevCellIds = (acc[gridRow.gridLayoutId] ??= new Set());
        const currentCellIds = gridRow.gridColumns.flatMap((c) =>
          c.gridElements
            .filter((element) => element.type === GridElementType.CELL)
            .map((e) => e.entityId as CellId),
        );
        for (const cellId of currentCellIds) {
          prevCellIds.add(cellId);
        }
        return acc;
      },
      {},
    );

    return typedObjectEntries(tabIdToCellIds).reduce<
      Record<GridLayoutId, "ERRORED" | "RUNNING" | "IDLE">
    >((acc, [tabId, cellIds]) => {
      const runStates = Array.from(cellIds).map(
        (cellId) => cellIdToRunState[cellId],
      );
      const runStateSet = new Set(runStates);

      let tabExecutionState: "ERRORED" | "RUNNING" | "IDLE";

      if (
        runStateSet.has(CellExecutionState.QUEUED) ||
        runStateSet.has(CellExecutionState.RUNNING)
      ) {
        tabExecutionState = "RUNNING";
      } else if (runStateSet.has(CellExecutionState.ERRORED)) {
        tabExecutionState = "ERRORED";
      } else {
        tabExecutionState = "IDLE";
      }

      acc[tabId] = tabExecutionState;
      return acc;
    }, {});
  },
);

export const selectEntityIdToTabIds = createSelector(
  [(rows: GridRowMP[]) => rows],
  (gridRows) =>
    gridRows.reduce<Record<GridElementEntityId, Set<GridLayoutId>>>(
      (acc, row) => {
        const elements = row.gridColumns.flatMap((col) => col.gridElements);
        for (const element of elements) {
          const prevSet = (acc[element.entityId] ??= new Set());
          prevSet.add(row.gridLayoutId);
        }
        return acc;
      },
      {},
    ),
);

export function getFirstTabIdContainingEntityId(
  gridRows: GridRowMP[],
  entityId: GridElementEntityId,
): GridLayoutId | undefined {
  return gridRows.find((r) =>
    r.gridColumns
      .flatMap((c) => c.gridElements)
      .some((e) => e.entityId === entityId),
  )?.gridLayoutId;
}

export interface SelectTabOptions {
  selectedStaticCellId?: StaticCellId;
  skipScrollDelay?: boolean;
}

export type SelectTabContainingEntityId = (
  entityId: GridElementEntityId,
  options?: SelectTabOptions,
) => void;

export function useSelectTabContainingEntityId(
  hexVersionId?: HexVersionId,
): SelectTabContainingEntityId {
  const getGridRows = useGridRowsGetter({ hexVersionId });
  const selectTab = useSelectTab(hexVersionId);

  return useCallback(
    (entityId, options) => {
      const gridRows = getGridRows();
      const firstTabWithEntityId = getFirstTabIdContainingEntityId(
        gridRows,
        entityId,
      );

      if (firstTabWithEntityId != null) {
        selectTab(firstTabWithEntityId, options);
      }
    },
    [getGridRows, selectTab],
  );
}

export function useSelectedTabId(): GridLayoutId | undefined {
  const queryParams = useQueryParams();
  return useGridLayoutsSelector({
    selector: (state) => selectSelectedTabId(state, queryParams),
  });
}

export function useSelectedTabIdGetter(): () => GridLayoutId | undefined {
  const getTabs = useGridLayoutsGetter();

  return useCallback(() => {
    const location = window.location;
    const queryParams = new URLSearchParams(location.search);
    const tabs = getTabs();

    return selectSelectedTabId(tabs, queryParams);
  }, [getTabs]);
}

export function useGetCurrentTabForQueryParams(): () => string | null {
  const getTabs = useGridLayoutsGetter();
  const getSelectedTabId = useSelectedTabIdGetter();

  return useCallback(() => {
    const tabs = getTabs();
    const selectedTabId = getSelectedTabId();
    const primaryTabId = selectPrimaryTabId(tabs);
    if (selectedTabId == null || selectedTabId === primaryTabId) {
      return null;
    }
    const idToSlugs = selectTabIdToSlug(tabs);
    const selectedTabSlug = idToSlugs[selectedTabId];
    if (selectedTabSlug == null) {
      return null;
    }
    return selectedTabSlug;
  }, [getTabs, getSelectedTabId]);
}

export type SelectTab = (
  gridLayoutId: GridLayoutId,
  options?: SelectTabOptions,
) => void;

/**
 * `hexVersionId` should be provided if this function is used **outside** of the project
 * context of the tab being navigated to. The caller is responsible for knowing that the
 * necessary state is in redux.
 */
export function useSelectTab(hexVersionId?: HexVersionId): SelectTab {
  const location = useLocation();
  const navigate = useNavigate();
  const getTabs = useGridLayoutsGetter({ hexVersionId });
  const getSelectedTabId = useSelectedTabIdGetter();

  return useCallback(
    (selectedGridLayoutId, options) => {
      // Read fresh location synchronously from window to avoid stale location state
      const queryParams = new URLSearchParams(window.location.search);
      const tabs = getTabs();

      const idToSlugs = selectTabIdToSlug(tabs);
      const selectedTabSlug = idToSlugs[selectedGridLayoutId];
      if (selectedTabSlug == null) {
        return;
      }

      // Early return if already on the target tab and no extra params to set.
      const primaryTabId = selectPrimaryTabId(tabs);
      if (options == null) {
        if (hexVersionId == null) {
          if (selectedGridLayoutId === getSelectedTabId()) {
            return;
          }
        } else {
          const currentTabSlug = queryParams.get(TAB_QUERY_PARAM);
          const targetIsDefault = selectedGridLayoutId === primaryTabId;
          const alreadyOnTab = targetIsDefault
            ? currentTabSlug == null
            : currentTabSlug === selectedTabSlug;
          if (alreadyOnTab) {
            return;
          }
        }
      }

      queryParams.delete(TAB_QUERY_PARAM);
      if (selectedGridLayoutId !== primaryTabId) {
        queryParams.append(TAB_QUERY_PARAM, selectedTabSlug);
      }

      if (options?.selectedStaticCellId != null) {
        queryParams.set("selectedStaticCellId", options.selectedStaticCellId);
      }

      const nextState: SelectionNavigationState | null =
        options?.skipScrollDelay
          ? mergeSelectionNavigationState(location.state, {
              skipScrollDelay: true,
            })
          : null;

      navigate(
        { search: queryParams.toString() },
        nextState != null ? { state: nextState } : undefined,
      );
    },
    [getSelectedTabId, getTabs, hexVersionId, location.state, navigate],
  );
}

export function useInsertNewTab(): () => GridLayoutId {
  const getTabs = useGridLayoutsGetter();
  const { dispatchAO } = useHexVersionAOContext();

  return useCallback(() => {
    const tabs = getTabs();

    const existingTabNames = new Set(tabs.map((tab) => tab.name));
    let count = tabs.length + 1;
    let testName = `Tab ${count}`;
    while (existingTabNames.has(testName)) {
      count++;
      testName = `Tab ${count}`;
    }

    const newTabId = uuid() as GridLayoutId;

    dispatchAO(
      CREATE_GRID_LAYOUT.create({
        gridLayoutId: newTabId,
        name: testName,
        insertAt: fractionalIndexMidpoint95(
          tabs[tabs.length - 1]?.order ?? FRACTIONAL_INDEX_START,
          FRACTIONAL_INDEX_END,
        ),
        staticId: uuid() as StaticGridLayoutId,
      }),
    );

    return newTabId;
  }, [dispatchAO, getTabs]);
}

export type DuplicateTab = (
  gridLayoutId: GridLayoutId,
) => GridLayoutId | undefined;

function getDuplicateTabName(oldName: string): string {
  const defaultNewName = `${oldName} (Copy)`;

  if (oldName.endsWith("(Copy)")) {
    const trimmedName = oldName.replace(/\(Copy\)$/, "");
    return `${trimmedName} (Copy 2)`;
  }

  const match = oldName.match(/\(Copy (?<digits>\d+)\)$/);

  if (match == null || match.index == null) return defaultNewName;

  const digitsStr = match.groups?.digits;
  if (digitsStr == null) return defaultNewName;

  const digitVal = parseInt(digitsStr);
  if (isNaN(digitVal)) return defaultNewName;

  return `${oldName.substring(0, match.index)} (Copy ${digitVal + 1})`;
}

export function useDuplicateTab(): DuplicateTab {
  const getGridRows = useGridRowsGetter();
  const getGridLayouts = useGridLayoutsGetter();
  const { dispatchAO } = useHexVersionAOContext();

  return useCallback(
    (gridLayoutId: GridLayoutId) => {
      const tabs = getGridLayouts();
      const tabToDuplicateIdx = tabs.findIndex((l) => l.id === gridLayoutId);

      if (tabToDuplicateIdx === -1) {
        return undefined;
      }

      const tabToDuplicate = tabs[tabToDuplicateIdx];

      const gridRows = getGridRows();
      const rows = gridRows.filter(
        (row) => row.gridLayoutId === gridLayoutId && row.deletedDate == null,
      );

      const newTabId = uuid() as GridLayoutId;

      dispatchAO([
        CREATE_GRID_LAYOUT.create({
          gridLayoutId: newTabId,
          name: getDuplicateTabName(tabToDuplicate.name),
          insertAt: fractionalIndexMidpoint95(
            tabToDuplicate.order,
            tabs[tabToDuplicateIdx + 1]?.order ?? FRACTIONAL_INDEX_END,
          ),
          staticId: uuid() as StaticGridLayoutId,
        }),
        ...rows.map((row) => {
          const gridRow = gridRowMPToGridRow(row);
          gridRow.id = uuid() as GridRowId;
          for (const col of gridRow.gridColumns) {
            col.id = uuid() as GridColumnId;
            for (const ele of col.gridElements) {
              ele.id = uuid() as GridElementId;
            }
          }

          return CREATE_GRID_ROW.create({
            gridLayoutId: newTabId,
            gridRowId: gridRow.id,
            gridColumns: gridRow.gridColumns,
            insertAt: gridRow.order,
          });
        }),
      ]);

      return newTabId;
    },
    [dispatchAO, getGridRows, getGridLayouts],
  );
}

export type DeleteTab = (gridLayoutId: GridLayoutId) => void;

export function useDeleteTab(): DeleteTab {
  const dispatch = useDispatch();
  const getGridRows = useGridRowsGetter();
  const getGridLayouts = useGridLayoutsGetter();
  const { dispatchAO } = useHexVersionAOContext();

  return useCallback(
    (gridLayoutId: GridLayoutId) => {
      const layouts = getGridLayouts();
      const gridLayoutToDelete = layouts.find((l) => l.id === gridLayoutId);

      if (gridLayoutToDelete == null) {
        return;
      }

      const gridRows = getGridRows();
      const elementCount = gridRows
        .filter((row) => row.gridLayoutId === gridLayoutId)
        .flatMap((row) =>
          row.gridColumns.flatMap((column) => column.gridElements),
        ).length;

      if (elementCount > 0) {
        dispatch(
          setDeleteTabDialog({
            open: true,
            gridLayoutToDelete,
            elementCount,
          }),
        );
      } else {
        dispatchAO(DELETE_GRID_LAYOUT.create({ gridLayoutId }));
      }
    },
    [dispatch, dispatchAO, getGridRows, getGridLayouts],
  );
}

export function useTabIdToExecutionState(): Partial<
  Record<GridLayoutId, "ERRORED" | "RUNNING" | "IDLE">
> {
  const { hexVersionId } = useProjectContext();
  const { appSessionId } = useSessionContext();

  return useSelector((state) =>
    selectTabIdToCellExecutionState(state, hexVersionId, appSessionId),
  );
}

/** Used for presentation mode */
export function useSelectNextTab(): (direction: "RIGHT" | "LEFT") => void {
  const navigate = useNavigate();
  const getTabs = useGridLayoutsGetter();
  const getSelectedTabId = useSelectedTabIdGetter();

  return useCallback(
    (direction: "RIGHT" | "LEFT") => {
      // Read fresh location synchronously from window to avoid stale location state
      const queryParams = new URLSearchParams(window.location.search);
      const tabs = getTabs();

      const currentlySelectedTab = getSelectedTabId();
      const sortedTabs = tabs.length > 1 ? [TITLE_SCREEN, ...tabs] : tabs;

      const currentTabIndex = sortedTabs.findIndex(
        (tab) => tab.id === currentlySelectedTab,
      );

      const offset = direction === "RIGHT" ? 1 : -1;
      const nextTabIndex = currentTabIndex + offset;
      if (nextTabIndex >= sortedTabs.length || nextTabIndex < 0) {
        return;
      }

      const nextTab = sortedTabs[nextTabIndex];

      const idToSlugs = selectTabIdToSlug(tabs);
      const selectedTabSlug =
        nextTab.id === TITLE_SCREEN.id
          ? TITLE_SCREEN_SLUG
          : idToSlugs[nextTab.id];
      if (selectedTabSlug == null) {
        return;
      }

      queryParams.delete(TAB_QUERY_PARAM);

      const primaryTabId = selectPrimaryTabId(tabs);
      if (nextTab.id !== primaryTabId) {
        queryParams.append(TAB_QUERY_PARAM, selectedTabSlug);
      }

      navigate({ search: queryParams.toString() });
    },
    [getSelectedTabId, getTabs, navigate],
  );
}
